/*
 * catalog.js — bidirectional lookup over Shopify-native product/bundle/
 * collection relationships emitted server-side via the catalog-data-emit
 * Liquid snippet.
 *
 * Data source: window.__shopifyCatalog.products[<handle>] populated by
 * inline <script> tags rendered for every product the page touches
 * (collection cards, PDP, featured products, etc.).
 *
 * Public API on window.Catalog:
 *   bundlesContaining(handle)             -> [{bundleHandle, position, quantity}]
 *                                            Bundles in which `handle` is a component.
 *   bundlesContainingAsFirst(handle)      -> [{bundleHandle, quantity}]
 *                                            Bundles where `handle` is the PRIMARY (position 0)
 *                                            component -- the cascade anchor.
 *   componentsOf(handle)                  -> [{handle, title, quantity}]
 *                                            Components of `handle` (if it's a bundle).
 *   firstComponentOf(handle)              -> {handle, title, quantity} | null
 *   mainProductOf(handle)                 -> componentHandle | null
 *                                            Explicit bundle hero (custom.bundle_main_product),
 *                                            which is also emitted at components[0].
 *   includedInBundles(handle)             -> [bundleHandle, ...]
 *                                            Server-emitted reverse (custom.included_in_bundles);
 *                                            complete even when no bundle is on the page.
 *   collectionsOf(handle)                 -> [collectionHandle, ...]
 *   productsInCollection(collectionHandle)-> [productHandle, ...]
 *   tagsOf(handle)                        -> [tag, ...] (lowercased)
 *   productsWithTag(tag)                  -> [productHandle, ...]
 *   hasTag(handle, tag)                   -> Boolean
 *   isBundle(handle)                      -> Boolean
 *   get(handle)                           -> full product record | null
 *   knownHandles()                        -> [handle, ...] (all products with emitted data)
 *
 * Reverse indices (`_bundlesOf`, `_productsInCollection`) are built
 * LAZILY on first call and cached on the catalog object itself. Adding
 * a new product (re-emit) invalidates them so the next lookup rebuilds.
 * Cheap: O(N * avg components per product) where N is currently-loaded
 * products. Typical page: ~25 products, ~6 components per bundle -> 150
 * operations, sub-millisecond.
 */

(function () {
    'use strict';

    function _ensure() {
        if (!window.__shopifyCatalog) {
            window.__shopifyCatalog = {
                products: {},
                _bundlesOf: null,
                _productsInCollection: null,
                _productsWithTag: null
            };
        }
        return window.__shopifyCatalog;
    }

    function _normTag(t) {
        return String(t || '').toLowerCase().trim();
    }

    function _buildIndices(cat) {
        var bundlesOf = Object.create(null);          // componentHandle -> [{bundleHandle, position, quantity}]
        var inCollection = Object.create(null);       // collectionHandle -> [productHandle, ...]
        var withTag = Object.create(null);            // tag (lowercased) -> [productHandle, ...]
        var handles = Object.keys(cat.products);
        for (var i = 0; i < handles.length; i++) {
            var h = handles[i];
            var p = cat.products[h];
            if (!p) continue;
            // Reverse: each collection -> products in it.
            if (Array.isArray(p.collections)) {
                for (var k = 0; k < p.collections.length; k++) {
                    var ch = p.collections[k];
                    if (!inCollection[ch]) inCollection[ch] = [];
                    if (inCollection[ch].indexOf(h) === -1) inCollection[ch].push(h);
                }
            }
            // Reverse: each tag -> products carrying it.
            if (Array.isArray(p.tags)) {
                for (var t = 0; t < p.tags.length; t++) {
                    var tg = _normTag(p.tags[t]);
                    if (!tg) continue;
                    if (!withTag[tg]) withTag[tg] = [];
                    if (withTag[tg].indexOf(h) === -1) withTag[tg].push(h);
                }
            }
            // Reverse: each component -> bundles that contain it (with position).
            if (p.isBundle && Array.isArray(p.components)) {
                for (var j = 0; j < p.components.length; j++) {
                    var c = p.components[j];
                    if (!c || !c.handle) continue;
                    if (!bundlesOf[c.handle]) bundlesOf[c.handle] = [];
                    bundlesOf[c.handle].push({
                        bundleHandle: h,
                        position: j,
                        quantity: c.quantity || 1
                    });
                }
            }
        }
        // Seed reverse edges from each product's server-emitted
        // includedInBundles (custom.included_in_bundles) so a component's
        // OWN page knows its bundles even when those bundle products weren't
        // rendered here. position=null marks an edge we couldn't derive a
        // component order for (the bundle wasn't on the page).
        for (var m = 0; m < handles.length; m++) {
            var hm = handles[m];
            var pm = cat.products[hm];
            if (!pm || !Array.isArray(pm.includedInBundles)) continue;
            for (var n = 0; n < pm.includedInBundles.length; n++) {
                var bh = pm.includedInBundles[n];
                if (!bh) continue;
                if (!bundlesOf[hm]) bundlesOf[hm] = [];
                var dup = false;
                for (var q = 0; q < bundlesOf[hm].length; q++) {
                    if (bundlesOf[hm][q].bundleHandle === bh) { dup = true; break; }
                }
                if (!dup) bundlesOf[hm].push({ bundleHandle: bh, position: null, quantity: 1 });
            }
        }
        cat._bundlesOf = bundlesOf;
        cat._productsInCollection = inCollection;
        cat._productsWithTag = withTag;
    }

    function _withIndices() {
        var cat = _ensure();
        if (cat._bundlesOf == null || cat._productsInCollection == null || cat._productsWithTag == null) {
            _buildIndices(cat);
        }
        return cat;
    }

    var Catalog = {
        // ── Forward (bundle -> its components) ─────────────────────────
        componentsOf: function (handle) {
            var p = _ensure().products[handle];
            return (p && Array.isArray(p.components)) ? p.components.slice() : [];
        },
        firstComponentOf: function (handle) {
            var comps = Catalog.componentsOf(handle);
            return comps.length ? comps[0] : null;
        },
        // Explicit hero of a bundle (custom.bundle_main_product). Components
        // are emitted main-first, so this falls back to the first component.
        mainProductOf: function (handle) {
            var p = _ensure().products[handle];
            if (p && p.mainProduct) return p.mainProduct;
            var first = Catalog.firstComponentOf(handle);
            return first ? first.handle : null;
        },
        // Server-emitted reverse list (custom.included_in_bundles): the
        // bundles this product is a component of. Page-independent.
        includedInBundles: function (handle) {
            var p = _ensure().products[handle];
            return (p && Array.isArray(p.includedInBundles)) ? p.includedInBundles.slice() : [];
        },

        // ── Reverse (component -> bundles containing it) ───────────────
        bundlesContaining: function (componentHandle) {
            var cat = _withIndices();
            var arr = cat._bundlesOf[componentHandle];
            return arr ? arr.slice() : [];
        },
        bundlesContainingAsFirst: function (componentHandle) {
            return Catalog.bundlesContaining(componentHandle)
                .filter(function (b) { return b.position === 0; });
        },

        // ── Collection membership (both directions) ────────────────────
        collectionsOf: function (handle) {
            var p = _ensure().products[handle];
            return (p && Array.isArray(p.collections)) ? p.collections.slice() : [];
        },
        productsInCollection: function (collectionHandle) {
            var cat = _withIndices();
            var arr = cat._productsInCollection[collectionHandle];
            return arr ? arr.slice() : [];
        },

        // ── Tag membership (both directions) ───────────────────────────
        tagsOf: function (handle) {
            var p = _ensure().products[handle];
            return (p && Array.isArray(p.tags)) ? p.tags.slice() : [];
        },
        productsWithTag: function (tag) {
            var cat = _withIndices();
            var arr = cat._productsWithTag[_normTag(tag)];
            return arr ? arr.slice() : [];
        },
        hasTag: function (handle, tag) {
            var p = _ensure().products[handle];
            if (!p || !Array.isArray(p.tags)) return false;
            var needle = _normTag(tag);
            if (!needle) return false;
            for (var i = 0; i < p.tags.length; i++) {
                if (_normTag(p.tags[i]) === needle) return true;
            }
            return false;
        },

        // ── Utilities ──────────────────────────────────────────────────
        isBundle: function (handle) {
            var p = _ensure().products[handle];
            return !!(p && p.isBundle);
        },
        get: function (handle) {
            return _ensure().products[handle] || null;
        },
        knownHandles: function () {
            return Object.keys(_ensure().products);
        },
        // Diagnostic: dump the current catalog state. Cheap; for DevTools.
        debug: function () {
            var cat = _ensure();
            return {
                productCount: Object.keys(cat.products).length,
                bundleCount: Object.values(cat.products).filter(function (p) { return p.isBundle; }).length,
                indicesBuilt: cat._bundlesOf != null,
                sample: Object.keys(cat.products).slice(0, 5).map(function (h) {
                    var p = cat.products[h];
                    return {
                        handle: h,
                        isBundle: p.isBundle,
                        components: (p.components || []).length,
                        collections: (p.collections || []).length,
                        tags: (p.tags || []).length
                    };
                })
            };
        }
    };

    window.Catalog = Catalog;
})();
